import Phaser from 'phaser';
import { PLAYER, walkSpeedMult } from '@/config/balance.config';
import { AVATAR_SKINS_BY_ID, DEFAULT_STARTER_SKIN_ID } from '@/config/avatars.config';
import { PlayerAnimator } from '@/game/fx/PlayerAnimator';
import { assetBundleManager } from '@/game/assets/AssetBundleManager';
import { saveManager } from '@/save';
import { eventBus } from '@/shared/EventBus';
import type { InputSystem } from '@/game/systems/InputSystem';
import type { AvatarSkinId } from '@/domain/types/avatar';

const SPRITE_DISPLAY_SIZE = 80;        // px — docelowy bok gracza na ekranie

/**
 * Encja gracza.
 *
 * Renderuje wybrany skin avatara (PNG z assets/avatars/) z fallbackiem na
 * niebieski prostokąt — żeby gra działała zanim wgrasz PNG-ki.
 *
 * Ruch: arcade physics, velocity z InputSystem. Sprite ma origin bottom-center
 * (postać stoi stopami na linii ziemi — cień się zgadza).
 *
 * Skin pochodzi z PlayerSave.profile.avatar.skinId (Step 2+). Dla teraz
 * hard-coded `DEFAULT_STARTER_SKIN_ID` (chłopiec). Selektor avatara przy
 * pierwszym uruchomieniu dorzucimy w Step 2.
 */
export class Player {
  readonly sprite: (Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle) & {
    body: Phaser.Physics.Arcade.Body;
  };

  readonly animator: PlayerAnimator | null;

  speed: number = PLAYER.baseSpeed;

  private prevX: number;
  private prevY: number;
  private avatarChangeSeq = 0;

  /**
   * Ustawia prędkość ruchu na podstawie poziomu upgrade'u `walk_speed`.
   * Wywoływane przez WorldScene na starcie sceny (initial level z save'a)
   * oraz przy każdym `upgrade:purchased` evencie — gracz odczuwa boost natychmiast.
   */
  setSpeedFromWalkSpeedLevel(level: number): void {
    this.speed = PLAYER.baseSpeed * walkSpeedMult(level);
  }

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    private readonly input: InputSystem,
    skinId: AvatarSkinId = DEFAULT_STARTER_SKIN_ID,
  ) {
    const skin = AVATAR_SKINS_BY_ID[skinId];
    const textureKey = skin?.art.textureKey;
    const useSprite = textureKey != null && scene.textures.exists(textureKey);

    if (useSprite) {
      const img = scene.add.image(x, y, textureKey!);
      // Origin bottom-center: postać stoi stopami na (x, y)
      img.setOrigin(0.5, 1.0);
      const maxSide = Math.max(img.width, img.height);
      const baseScale = (SPRITE_DISPLAY_SIZE / maxSide) * (skin!.art.scale ?? 1);
      img.setScale(baseScale);
      img.setDepth(6);

      scene.physics.add.existing(img);
      const body = (img as unknown as { body: Phaser.Physics.Arcade.Body }).body;
      body.setCollideWorldBounds(true);

      this.sprite = img as typeof this.sprite;
      this.animator = new PlayerAnimator(scene, img, baseScale, x, y);
    } else {
      const rect = scene.add.rectangle(x, y, 40, 48, 0x4a6cf7);
      rect.setStrokeStyle(3, 0x000000);
      rect.setDepth(6);
      scene.physics.add.existing(rect);
      const body = (rect as unknown as { body: Phaser.Physics.Arcade.Body }).body;
      body.setCollideWorldBounds(true);
      this.sprite = rect as typeof this.sprite;
      this.animator = null;
    }

    this.prevX = this.sprite.x;
    this.prevY = this.sprite.y;

    // Live-swap skina po zmianie w „Wygląd" (AvatarCustomizeModal). Sprzątanie
    // przez `destroy` sprite'a → odpina się automatycznie przy shutdownie sceny
    // (Phaser niszczy GameObjecty), więc działa w KAŻDEJ scenie bez per-scene wiring.
    const offAvatar = eventBus.on('avatar:changed', (e) => {
      const token = ++this.avatarChangeSeq;
      const activeScene = this.sprite.scene;
      if (!activeScene) return;
      void assetBundleManager.ensureCurrentPlayerAssets(activeScene, saveManager.getCurrent()).then((prepared) => {
        if (!this.sprite.scene || token !== this.avatarChangeSeq) return;
        if (this.setSkin(e.skinId as AvatarSkinId)) {
          assetBundleManager.finalizePreparedScope(
            prepared,
            activeScene.textures,
            activeScene.anims,
          );
        }
      });
    });
    this.sprite.once('destroy', offAvatar);
  }

  /**
   * Podmienia teksturę sprite'a gracza na nowy skin (in-place — sprite/animator
   * są readonly, więc nie tworzymy ich od nowa). No-op gdy tekstura niezaładowana
   * lub gracz jest na fallbacku-prostokącie. Zachowuje pozycję, body i animator.
   */
  setSkin(skinId: AvatarSkinId): boolean {
    const skin = AVATAR_SKINS_BY_ID[skinId];
    const textureKey = skin?.art.textureKey;
    if (!textureKey) return false;
    if (!(this.sprite instanceof Phaser.GameObjects.Image)) return false; // fallback-rect → brak swapu
    const scene = this.sprite.scene;
    if (!scene || !scene.textures.exists(textureKey)) return false;  // tekstura niezaładowana → zostaw

    const img = this.sprite as Phaser.GameObjects.Image;
    img.setTexture(textureKey);
    const maxSide = Math.max(img.width, img.height);
    const baseScale = (SPRITE_DISPLAY_SIZE / maxSide) * (skin?.art.scale ?? 1);
    img.setScale(baseScale);
    this.animator?.setBaseScale(baseScale);
    return true;
  }

  get x(): number { return this.sprite.x; }
  get y(): number { return this.sprite.y; }

  update(time = 0, dt = 0): void {
    const move = this.input.getMovement();
    this.sprite.body.setVelocity(move.x * this.speed, move.y * this.speed);

    if (this.animator) {
      const vx = dt > 0 ? (this.sprite.x - this.prevX) / dt : 0;
      const vy = dt > 0 ? (this.sprite.y - this.prevY) / dt : 0;
      this.prevX = this.sprite.x;
      this.prevY = this.sprite.y;

      this.animator.update(time, vx, vy, this.sprite.x, this.sprite.y);
    }
  }
}
